import { useEffect, useState } from 'react';
import { ExternalLink, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  extractPlaceId,
  isPlacesApiCompatible,
  resolvePlaceIdServer,
  useAddOutlet,
  type CompetitorBrand,
} from '@/hooks/useAggregatorCompetitive';

interface Props {
  open: boolean;
  onClose: () => void;
  brands: CompetitorBrand[];
  initialBrandSlug?: string;
}

export function AddOutletDialog({ open, onClose, brands, initialBrandSlug }: Props) {
  const [brandSlug, setBrandSlug] = useState(initialBrandSlug ?? '');
  useEffect(() => {
    if (open) setBrandSlug(initialBrandSlug ?? '');
  }, [open, initialBrandSlug]);
  const [city, setCity] = useState('Mumbai');
  const [locality, setLocality] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [googleUrl, setGoogleUrl] = useState('');
  const [swiggyUrl, setSwiggyUrl] = useState('');
  const [zomatoUrl, setZomatoUrl] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [justSaved, setJustSaved] = useState<string | null>(null);

  const add = useAddOutlet();
  const [resolving, setResolving] = useState(false);
  const [resolvedId, setResolvedId] = useState<string | null>(null);
  const [resolveErr, setResolveErr] = useState<string | null>(null);

  // Auto-resolve when the user pastes a URL we can't crack locally.
  useEffect(() => {
    setResolveErr(null);
    const inline = extractPlaceId(googleUrl);
    if (inline) {
      setResolvedId(inline);
      return;
    }
    setResolvedId(null);
    if (!googleUrl.trim() || !/^https?:\/\//.test(googleUrl.trim())) return;
    const timer = setTimeout(async () => {
      setResolving(true);
      const res = await resolvePlaceIdServer(googleUrl.trim());
      setResolving(false);
      if ('place_id' in res) setResolvedId(res.place_id);
      else setResolveErr(res.error);
    }, 500);
    return () => clearTimeout(timer);
  }, [googleUrl]);

  const placeId = resolvedId;
  const placeIdOk = isPlacesApiCompatible(placeId);

  if (!open) return null;

  async function submit() {
    setErr(null);
    if (!brandSlug) return setErr('Pick a brand.');
    if (!locality.trim()) return setErr('Locality is required.');
    if (googleUrl && !placeId) {
      return setErr(
        'Could not extract a place_id from that URL. Try the Place ID Finder link below.',
      );
    }
    if (placeId && !placeIdOk) {
      return setErr(
        'This looks like a legacy FTID, which the Places API (New) rejects. Use the Place ID Finder to get a ChIJ… ID.',
      );
    }
    try {
      await add.mutateAsync({
        brand_slug: brandSlug,
        city: city.trim(),
        locality: locality.trim(),
        display_name: displayName.trim() || null,
        swiggy_url: swiggyUrl.trim() || null,
        zomato_url: zomatoUrl.trim() || null,
        google_place_id: placeId ?? null,
      });
      // Keep the dialog open + brand/city sticky so you can rapid-fire
      // multiple outlets for the same brand without re-selecting.
      const savedLabel = displayName.trim() || locality.trim();
      setJustSaved(savedLabel);
      setLocality('');
      setDisplayName('');
      setGoogleUrl('');
      setSwiggyUrl('');
      setZomatoUrl('');
      setResolvedId(null);
    } catch (e) {
      setErr((e as Error).message);
    }
  }

  const inputCls =
    'h-9 w-full rounded-md border border-border bg-background px-2.5 text-sm';

  return (
    <div className="rounded-lg border border-border/60 bg-card/50 p-3 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">Add competitor outlet</h3>
        <Button size="sm" variant="ghost" onClick={onClose}>
          Done
        </Button>
      </div>
      {justSaved && (
        <div className="rounded-md border border-emerald-500/30 bg-emerald-500/10 p-2 text-2xs text-emerald-700 dark:text-emerald-400">
          ✓ Saved <strong>{justSaved}</strong>. Brand + city kept — add another below, or hit Done.
        </div>
      )}

      <div className="grid gap-2 md:grid-cols-2">
        <Field label="Brand">
          <select
            value={brandSlug}
            onChange={(e) => setBrandSlug(e.target.value)}
            className={inputCls}
          >
            <option value="">Pick a brand</option>
            {brands.map((b) => (
              <option key={b.slug} value={b.slug}>
                {b.display_name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Display name (optional)">
          <input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Boojee Bandra"
            className={inputCls}
          />
        </Field>
        <Field label="City">
          <input value={city} onChange={(e) => setCity(e.target.value)} className={inputCls} />
        </Field>
        <Field label="Locality">
          <input
            value={locality}
            onChange={(e) => setLocality(e.target.value)}
            placeholder="Bandra"
            className={inputCls}
          />
        </Field>
        <Field
          label="Google Maps URL or place_id"
          full
          hint={
            <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
              {resolving && (
                <span className="flex items-center gap-1 text-muted-foreground">
                  <Loader2 className="h-3 w-3 animate-spin" /> Resolving…
                </span>
              )}
              {placeId && placeIdOk && (
                <span className="text-emerald-500">
                  ✓ {placeId.slice(0, 40)}
                  {placeId.length > 40 ? '…' : ''}
                </span>
              )}
              {placeId && !placeIdOk && (
                <span className="text-amber-500">
                  Got legacy FTID ({placeId}) — Places API needs a ChIJ… ID. Use the finder →
                </span>
              )}
              {!placeId && !resolving && !resolveErr && (
                <span className="text-muted-foreground">
                  Paste a Google Maps URL (short URLs auto-resolve) or a ChIJ… place_id.
                </span>
              )}
              {resolveErr && <span className="text-rose-500">{resolveErr}</span>}
              <a
                href="https://developers.google.com/maps/documentation/places/web-service/place-id"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-0.5 text-primary hover:underline"
              >
                Place ID Finder <ExternalLink className="h-2.5 w-2.5" />
              </a>
            </span>
          }
        >
          <input
            value={googleUrl}
            onChange={(e) => setGoogleUrl(e.target.value)}
            placeholder="https://maps.app.goo.gl/… or ChIJ…"
            className={inputCls}
          />
        </Field>
        <Field label="Swiggy URL (optional)">
          <input
            value={swiggyUrl}
            onChange={(e) => setSwiggyUrl(e.target.value)}
            placeholder="https://www.swiggy.com/restaurants/…"
            className={inputCls}
          />
        </Field>
        <Field label="Zomato URL (optional)">
          <input
            value={zomatoUrl}
            onChange={(e) => setZomatoUrl(e.target.value)}
            placeholder="https://www.zomato.com/…"
            className={inputCls}
          />
        </Field>
      </div>

      {err && (
        <div className="rounded-md bg-rose-500/10 p-2 text-2xs text-rose-500">{err}</div>
      )}
      <div className="flex justify-end">
        <Button size="sm" onClick={submit} disabled={add.isPending}>
          {add.isPending && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
          Add outlet
        </Button>
      </div>
    </div>
  );
}

function Field({
  label,
  hint,
  full,
  children,
}: {
  label: string;
  hint?: React.ReactNode;
  full?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className={full ? 'md:col-span-2' : ''}>
      <label className="mb-1 block text-2xs text-muted-foreground">{label}</label>
      {children}
      {hint && <div className="mt-1 text-2xs text-muted-foreground">{hint}</div>}
    </div>
  );
}
